import React, { useState } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { ColorRing } from 'react-loader-spinner';
import API_BASE_URL from '../../../../Constants';

/**
 * A “Create Service Company” modal that mimics 
 * the style of your building form:
 * - Top bar with a “close/arrow back” & “expand” 
 * - Second row with “Cancel/Save”
 * - Fields for all the service company entity
 */
export default function ServiceCompanyEntryModal({
  open,
  onClose,
  refreshCompanies, // callback to re-fetch after creation
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Local form fields
  const [formData, setFormData] = useState({
    status: '',
    type: '',
    companyName: '',
    addressCareOf: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
    contact: '',
    mainPhone: '',
    otherPhone: '',
    fax: '',
    email: '',
    director: '',
    companyCertificationNumber: '',
    certificationByType: '',
    certificationPhone: '',
    certificationEmail: '',
    certifiedDate: '',
    customer: '',
    customerID: '',
    alternateOtherPhone: '',
    otherEmail: '',
  });

  // Basic validation errors
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');

  if (!open) return null; // Don’t render if not open

  function handleClose() {
    onClose(); 
  }

  // handle user typing
  function handleFieldChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: '' })); 
  }

  function validateFields() {
    const newErrors = {};
    if (!formData.companyName) {
      newErrors.companyName = 'Company name is required';
    }
    if (!formData.type) {
      newErrors.type = 'Type is required';
    }
    return newErrors;
  }

  async function handleSave() {
    // Validate first
    const newErrors = validateFields();
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      alert('Please fix validation errors first.');
      return;
    }
    setIsLoading(true);
    setErrorMessage('');
    try {
      // POST to create
      const response = await fetch(`${API_BASE_URL}/api/inspections/service-companies`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        throw new Error(`Create failed, status: ${response.status}`);
      }
      alert('Service Company created successfully!');
      if (refreshCompanies) {
        refreshCompanies();
      }
      onClose();
    } catch (err) {
      console.error('Error creating =>', err);
      setErrorMessage(err.message);
    } finally {
      setIsLoading(false);
    }
  }

  // Helper to render an input row
  function renderInputRow(label, fieldName, required = false) {
    return (
      <div className="mb-2">
        <label className="font-semibold text-sm mb-1 block">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <input
          name={fieldName}
          value={formData[fieldName] ?? ''}
          onChange={handleFieldChange}
          className="border border-gray-300 rounded p-1 w-full"
        />
        {errors[fieldName] && (
          <p className="text-red-500 text-sm mt-1">{errors[fieldName]}</p>
        )}
      </div>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        style: {
          width: isExpanded ? '85%' : '700px',
          height: '80%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      style={{ zIndex: 1400 }}
    >
      {/* Header */}
      <div className="sticky top-0 bg-white z-10 border-b border-gray-300">
        {/* Title row */}
        <div className="flex justify-between items-center p-4">
          <div className="flex items-center space-x-4 flex-grow">
            <button
              onClick={handleClose}
              className="p-2 rounded-full hover:bg-gray-200 transition"
              aria-label="Back"
            >
              <ArrowBackIcon />
            </button>
            <h2 className="text-2xl font-bold text-left">
              Add Service Company
            </h2>
          </div>
          {/* Expand Icon */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 rounded-full hover:bg-gray-200 transition"
            aria-label={isExpanded ? 'Shrink' : 'Expand'}
          >
            {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </button>
        </div>

        {/* Action buttons row */}
        <div className="flex space-x-4 p-4">
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-gray-300 text-black font-bold rounded-md hover:bg-gray-400 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-yellow-500 text-white font-bold rounded-md hover:bg-yellow-600 transition"
          >
            Save
          </button>
        </div>
      </div>

      {/* Main content */}
      <DialogContent dividers style={{ flex: '1 1 auto', overflow: 'auto' }}>
        {isLoading && (
          <div className="flex justify-center items-center pt-24">
            <ColorRing
              visible
              height="80"
              width="80"
              colors={['#033B5C','#033B5C','#033B5C','#033B5C','#033B5C']}
            />
          </div>
        )}
        {!isLoading && (
          <div className="space-y-4">
            {errorMessage && (
              <p className="text-red-600 font-semibold">{errorMessage}</p>
            )}

            {/* Group 1: Basic info */}
            <div className="grid grid-cols-2 gap-4">
              {renderInputRow('Status', 'status')}
              {renderInputRow('Type', 'type', true)}
              {renderInputRow('Company Name', 'companyName', true)}
            </div>

            {/* Group 2: Address fields */}
            <div className="border-t border-gray-300 pt-3">
              <h3 className="font-bold text-md mb-2">Address</h3>
              <div className="grid grid-cols-2 gap-4">
                {renderInputRow('Address Care Of', 'addressCareOf')}
                {renderInputRow('Address Line 1', 'addressLine1')}
                {renderInputRow('Address Line 2', 'addressLine2')}
                {renderInputRow('City', 'city')}
                {renderInputRow('State', 'state')}
                {renderInputRow('Zip', 'zip')}
              </div>
            </div>

            {/* Group 3: Contact block */}
            <div className="border-t border-gray-300 pt-3">
              <h3 className="font-bold text-md mb-2">Contact / Phones</h3>
              <div className="grid grid-cols-2 gap-4">
                {renderInputRow('Contact', 'contact')}
                {renderInputRow('Main Phone', 'mainPhone')}
                {renderInputRow('Other Phone', 'otherPhone')}
                {renderInputRow('Alternate Other Phone', 'alternateOtherPhone')}
                {renderInputRow('Fax', 'fax')}
                {renderInputRow('Email', 'email')}
                {renderInputRow('Other Email', 'otherEmail')}
              </div>
            </div>

            {/* Group 4: Director / Certification */}
            <div className="border-t border-gray-300 pt-3">
              <h3 className="font-bold text-md mb-2">Certification</h3>
              <div className="grid grid-cols-2 gap-4">
                {renderInputRow('Director', 'director')}
                {renderInputRow('Company Cert #', 'companyCertificationNumber')}
                {renderInputRow('Certification by / Type', 'certificationByType')}
                {renderInputRow('Certification Phone', 'certificationPhone')}
                {renderInputRow('Certification Email', 'certificationEmail')}
                {renderInputRow('Certified Date', 'certifiedDate')}
              </div>
            </div>

            {/* Group 5: Customer fields */}
            <div className="border-t border-gray-300 pt-3">
              <h3 className="font-bold text-md mb-2">Customer Info</h3>
              <div className="grid grid-cols-2 gap-4">
                {renderInputRow('Customer', 'customer')}
                {renderInputRow('Customer ID', 'customerID')}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
